import {useState} from "react";
import {Box, Collapse, Typography} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import {useSSTranslation} from "@/i18n/useSSTranslation";

type Props = {
	buildMetaLine: string | null;
	remoteFlagsLine: string | null;
	variant?: "app2" | "legacy";
};

/**
 * Collapsed-by-default footer for build metadata and DevCycle flags on Settings.
 */
export const SettingsBuildInfoCollapsible = ({
	buildMetaLine,
	remoteFlagsLine,
	variant = "app2"
}: Props) => {
	const {t} = useSSTranslation();
	const [open, setOpen] = useState(false);

	if (!buildMetaLine && !remoteFlagsLine) {
		return null;
	}

	const isLegacy = variant === "legacy";
	return (
		<Box
			sx={{
				mt: isLegacy ? 3 : 4,
				textAlign: "center",
				color: isLegacy ? undefined : "text.secondary",
				...(!isLegacy && {opacity: 0.65})
			}}
		>
			<Box
				onClick={() => setOpen((o) => !o)}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						setOpen((o) => !o);
					}
				}}
				role="button"
				tabIndex={0}
				aria-expanded={open}
				data-test-id="settings-build-info-toggle"
				sx={{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					gap: 0.25,
					cursor: "pointer",
					userSelect: "none",
					...(isLegacy && {opacity: 1}),
					"&:hover": {opacity: isLegacy ? 0.85 : 0.8}
				}}
			>
				<Typography variant="caption" sx={{letterSpacing: 0.2, color: 'rgba(255,255,255,0.7)'}}>
					{t("page_settings_build_info_toggle", "Build & feature flags")}
				</Typography>
				{open ? (
					<ExpandLess sx={{fontSize: 18, opacity: 0.85}} />
				) : (
					<ExpandMore sx={{fontSize: 18, opacity: 0.85}} />
				)}
			</Box>
			<Collapse in={open}>
				<Box sx={{mt: 1, color: 'text.secondary', letterSpacing: 0.2}}>
					{buildMetaLine && (
						<Typography variant="caption" component="p" sx={{m: 0, color: 'rgba(255,255,255,0.7)'}}>
							{buildMetaLine}
						</Typography>
					)}
					{remoteFlagsLine && (
						<Typography
							variant="caption"
							component="p"
							sx={{
								m: 0,
								mt: buildMetaLine ? 0.5 : 0,
								wordBreak: "break-word"
							}}
							color="rgba(255,255,255,0.7)"
						>
							{remoteFlagsLine}
						</Typography>
					)}
				</Box>
			</Collapse>
		</Box>
	);
};
